import { TRACK_IDS } from "../../../analytics/trackIds";
import React, { useEffect, useRef, useState } from "react";
import localizationStrings from "../../../statics/localization";
import SubtitleIcon from "../../../assets/icons/svg/SubtitleIcon";
import PlayerMenu from "./PlayerMenu";

const EMPTY_TRACKS = [];
const normalizeLanguage = (language) =>
  ({ tur: "tr", eng: "en", ori: "un" }[language] || language);
const trackLabel = (track) => {
  if (track.label === "default" || track.label === "main") return localizationStrings.audioOriginal;
  const language = normalizeLanguage(track.language);
  if (track.label && track.label !== "main" && track.label !== track.language)
    return track.label;
  return (
    {
      tr: localizationStrings.turkish,
      en: localizationStrings.english,
      ar: localizationStrings.arabic,
      tng: localizationStrings.descriptive,
      un: localizationStrings.audioOriginal
    }[language] ||
    track.label ||
    language
  );
};

const TrackControl = ({
  player,
  textTracks = EMPTY_TRACKS,
  audioTracks = EMPTY_TRACKS,
  liveui
}) => {
  const [open, setOpen] = useState(false);
  const [selectedTextTrack, setSelectedTextTrack] = useState(null);
  const [selectedAudioTrack, setSelectedAudioTrack] = useState(null);
  const button = useRef(null);
  const subtitles = textTracks.filter(
    (track) =>
      !track.kind || track.kind === "subtitles" || track.kind === "captions"
  );

  const selectSubtitle = (track, persist = true) => {
    subtitles.forEach((item) => {
      item.mode = item === track ? "showing" : liveui ? "hidden" : "disabled";
    });
    setSelectedTextTrack(track);
    if (persist)
      localStorage.setItem(
        "subtitleLang",
        track ? normalizeLanguage(track.language) : "off"
      );
  };
  const selectAudio = (track, persist = true) => {
    // Two tracks may share a language (e.g. main audio and audio description).
    audioTracks.forEach((item) => {
      item.enabled = item === track;
    });
    setSelectedAudioTrack(track);
    if (persist && track)
      localStorage.setItem("audioLang", normalizeLanguage(track.language));
  };
  useEffect(() => {
    const preferred = normalizeLanguage(localStorage.getItem("subtitleLang"));
    selectSubtitle(
      subtitles.find(
        (track) => normalizeLanguage(track.language) === preferred
      ) || null,
      false
    );
  }, [textTracks]);
  useEffect(() => {
    const preferred = normalizeLanguage(localStorage.getItem("audioLang"));
    selectAudio(
      audioTracks.find(
        (track) => normalizeLanguage(track.language) === preferred
      ) ||
        audioTracks.find((track) => track.enabled) ||
        audioTracks[0] ||
        null,
      false
    );
  }, [audioTracks]);
  useEffect(() => setOpen(false), [player]);
  const close = (restoreFocus) => {
    setOpen(false);
    if (restoreFocus && button.current) button.current.focus();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        data-track-id={TRACK_IDS.PLAYER_SUBTITLES}
        className="control-button"
        title={localizationStrings.playerOptions.track}
        aria-label={localizationStrings.playerOptions.track}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(!open)}
      >
        <SubtitleIcon />
      </button>
      {open && (
        <PlayerMenu
          player={player}
          anchorRef={button}
          label={localizationStrings.playerOptions.track}
          onClose={close}
        >
          <div className="atlas-track-columns">
            <div role="radiogroup" aria-label={localizationStrings.subtitle}>
              <h3>{localizationStrings.subtitle}</h3>
              <button
                type="button"
                role="radio"
                aria-checked={!selectedTextTrack}
                onClick={() => selectSubtitle(null)}
              >
                {localizationStrings.off}
              </button>
              {subtitles.map((track, index) => (
                <button
                  key={track.id || index}
                  type="button"
                  role="radio"
                  aria-checked={selectedTextTrack === track}
                  onClick={() => selectSubtitle(track)}
                >
                  {trackLabel(track)}
                </button>
              ))}
            </div>
            {audioTracks.length > 0 && (
              <div role="radiogroup" aria-label={localizationStrings.dubbing}>
                <h3>{localizationStrings.dubbing}</h3>
                {audioTracks.map((track, index) => (
                  <button
                    key={track.id || index}
                    type="button"
                    role="radio"
                    aria-checked={selectedAudioTrack === track}
                    onClick={() => selectAudio(track)}
                  >
                    {trackLabel(track)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </PlayerMenu>
      )}
    </>
  );
};
export default TrackControl;
